"use client"

import { useEffect, useState } from "react"
import { deleteProvider } from "../../services/providersApi"
import { useNavigate } from "react-router-dom"
import "./UserProfile.css"
import { toast } from "react-toastify"
import { deleteSelf, getLoggedUser } from "../../services/usersApi"
import { getUserData, saveJwtToken, saveUserData, removeJwtToken, removeUserData } from "../../utils/auth"
import { useAuth } from "../../utils/AuthContext"
import Spinner from "../Spinner/Spinner"
import ExportPdfButton from "./ExportPdfButton"
import { Trash2, Bell, Star, CornerDownRight } from "lucide-react"
import { GoogleAuthProvider, deleteUser } from "firebase/auth"
import { deleteProviderFiles } from "../../storageService";
import { auth } from "../../firebase";
import { editUserInfo } from "../../services/usersApi"
import { useLocations } from "../../utils/LocationContext"
import GeolocationField from "../GeolocationField/GeolocationField"
import UserPosts from "../Forum/UserPosts"
import PartnerBenefitsBox from "./PartnerBenefitsBox"
import ProviderDashboardBox from "./ProviderDashboardBox"


/** Fila de estrellas con soporte de media estrella (SVG con clipPath) */
const UserStarRow = ({ value, size = 16 }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        {[1, 2, 3, 4, 5].map((i) => {
            const full = value >= i
            const half = !full && value >= i - 0.5
            return (
                <span key={i} style={{ position: 'relative', display: 'inline-block', width: size, height: size, lineHeight: 0, verticalAlign: 'middle' }}>
                    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5">
                        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                    </svg>
                    {(full || half) && (
                        <svg
                            width={size} height={size} viewBox="0 0 24 24"
                            style={{ position: 'absolute', top: 0, left: 0, clipPath: half ? 'inset(0 50% 0 0)' : 'none' }}
                            fill="gold" stroke="gold" strokeWidth="1.5"
                        >
                            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                        </svg>
                    )}
                </span>
            )
        })}
    </span>
)

const UserProfile = () => {
    const { user, loading, handlePasswordReset, setLoading, handleDeleteAccount, handleReauthenticate, setUser, setUserData } =
        useAuth()
    const [userInfo, setUserInfo] = useState(null)
    const [profileLoading, setProfileLoading] = useState(true)
    const [showConfirmDelete, setShowConfirmDelete] = useState(false)
    const [showConfirmAccountDelete, setShowConfirmAccountDelete] = useState(false)
    const [password, setPassword] = useState("")
    const [showPasswordField, setShowPasswordField] = useState(false)
    const [isReauthenticating, setIsReauthenticating] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [activeTab, setActiveTab] = useState("usuario") // Nueva pestaña activa
    const [initialTabSet, setInitialTabSet] = useState(false)
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
    const [showProviderRegisterModal, setShowProviderRegisterModal] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [formData, setFormData] = useState({ name: "", location: "", birthDate: "", email: "", locationCoordinates: null })
    const [formErrors, setFormErrors] = useState({ name: null, location: null, birthDate: null })
    const [isValid, setIsValid] = useState(false)
    const navigate = useNavigate()
    const { detectLocationFromGPS, generateSmartLocation, SmartLocationService } = useLocations()


    useEffect(() => {
        const fetchUserInfo = async () => {
            setProfileLoading(true)
            try {
                const ui = await getLoggedUser()
                if (ui) {
                    setUserInfo(ui)
                    // Si el usuario es proveedor podríamos mantener pestaña en usuario por defecto.
                } else {
                    console.error("User info is null or undefined")
                }
            } catch (error) {
                console.error("Error fetching user info:", error)
                toast.error("Error al cargar la información del usuario")
            } finally {
                setProfileLoading(false)
            }
        }

        if (!loading && user) {
            fetchUserInfo()
        } else if (!loading && !user) {
            setProfileLoading(false)
        }
    }, [user, loading])


    useEffect(() => {
        // Sincronizar formData cuando cargue userInfo
        if (userInfo) {
            setFormData({
                name: userInfo.name || "",
                location: userInfo.location || "",
                birthDate: userInfo.birthDate || "",
                email: userInfo.email || "",
                locationCoordinates: null,
            })
        }
    }, [userInfo])

    const onPasswordReset = async () => {
        setLoading(true)
        try {
            await handlePasswordReset(userInfo.email)
            toast.success("Se ha enviado un correo para restablecer tu contraseña.")
        } catch (error) {
            console.error("Error enviando el correo de restablecimiento:", error)
            toast.error("Ocurrió un error al enviar el correo de restablecimiento.")
        } finally {
            setLoading(false)
        }
    }

    const handleProviderClick = (providerId, categoryType) => {
        navigate(`/${providerId}`, { state: { categoryType } })
    }

    const handleEditUserInfo = () => {
        setShowEditModal(false)
        navigate("/usuario/editar", { state: { userInfo } })
    }

    const handleEditProviderInfo = () => {
        setShowEditModal(false)
        if (userInfo && userInfo.providerRef && userInfo.providerType) {
            handleEditProvider(userInfo.providerRef, userInfo.providerType)
        } else {
            toast.error("No tienes un perfil de proveedor para editar")
        }
    }

    const handleEditProvider = (providerId, categoryType) => {
        navigate(`/${providerId}/editar`, { state: { providerId, categoryType } })
    }

    const handleNotificationsClick = () => {
        navigate("/usuario/notificaciones")
    }

    const handleDeleteProvider = async () => {
        setLoading(true)
        try {
            // Primero eliminar los archivos de Firebase Storage
            if (userInfo.providerRef && userInfo.providerType) {
                const filesDeleted = await deleteProviderFiles(userInfo.providerType, userInfo.providerRef)
                if (!filesDeleted) {
                    console.warn("No se pudieron eliminar todos los archivos del proveedor")
                }
            }

            // Luego eliminar el perfil del proveedor en la base de datos
            await deleteProvider(userInfo.providerRef, userInfo.providerType)

            // Actualizar los datos del usuario local
            const userData = getUserData()
            userData.isProvider = false
            userData.providerRef = null
            userData.providerType = null
            saveUserData(userData)

            toast.success("Perfil de proveedor eliminado correctamente.")
            window.location.reload()
        } catch (error) {
            console.error("Error al eliminar el perfil de proveedor:", error)
            toast.error("Hubo un error al eliminar el perfil de proveedor.")
        } finally {
            setShowConfirmDelete(false)
            setLoading(false)
        }
    }

    const handleDeleteAccountClick = () => {
        setShowConfirmAccountDelete(true)
    }

    const confirmDeleteAccount = async () => {
        setLoading(true)
        try {
            // Intentar eliminar la cuenta
            const result = await handleDeleteAccount()

            if (result.success) {
                // Eliminación exitosa, ya se manejó todo en el AuthContext
                toast.success("Tu cuenta ha sido eliminada correctamente.")
                navigate("/")
            } else if (result.requiresRecentLogin) {
                // Necesita reautenticación
                if (result.isGoogleUser) {
                    // Usuario de Google
                    setIsReauthenticating(true)
                    const googleProvider = new GoogleAuthProvider()
                    const reAuthResult = await handleReauthenticate(null, googleProvider)

                    if (reAuthResult.success) {
                        // Después de reautenticar con Google, intentar eliminar nuevamente
                        await performAccountDeletion()
                    } else {
                        toast.error("Error en la reautenticación con Google. Intente de nuevo.")
                    }
                    setIsReauthenticating(false)
                } else {
                    // Usuario de email/password - mostrar campo de contraseña SIN cerrar el modal
                    setShowPasswordField(true)
                    toast.info("Por seguridad, ingresa tu contraseña para confirmar la eliminación.")
                    // NO cerrar setShowConfirmAccountDelete aquí
                }
            } else {
                toast.error(result.message || "Ocurrió un error al eliminar la cuenta.")
            }
        } catch (error) {
            console.error("Error al eliminar la cuenta:", error)
            toast.error("Ocurrió un error al eliminar la cuenta")
        } finally {
            // Solo cerrar el modal si NO se está mostrando el campo de contraseña
            if (!showPasswordField && !isReauthenticating) {
                setShowConfirmAccountDelete(false)
            }
            setLoading(false)
        }
    }

    // Nueva función para realizar la eliminación completa después de la reautenticación
    const performAccountDeletion = async () => {
        try {
            setLoading(true)

            // Eliminar archivos de Storage si es proveedor PRIMERO
            if (userInfo && userInfo.isProvider && userInfo.providerRef && userInfo.providerType) {
                const filesDeleted = await deleteProviderFiles(userInfo.providerType, userInfo.providerRef)
                if (!filesDeleted) {
                    console.warn("No se pudieron eliminar todos los archivos del proveedor")
                }
            }

            // Eliminar del backend PRIMERO
            await deleteSelf()

            // Actualizar token después de reautenticación
            const currentUser = auth.currentUser
            if (currentUser) {
                const freshToken = await currentUser.getIdToken(true)
                saveJwtToken(freshToken)

                // Finalmente eliminar de Firebase Authentication
                await deleteUser(currentUser)
            }

            // Limpiar datos locales
            removeJwtToken()
            removeUserData()
            setUser(null)
            setUserData(null)

            toast.success("Tu cuenta ha sido eliminada correctamente.")
            navigate("/")
        } catch (error) {
            console.error("Error en eliminación completa:", error)
            toast.error("Error al completar la eliminación de la cuenta.")
        } finally {
            setLoading(false)
        }
    }

    const handlePasswordSubmit = async () => {
        if (!password.trim()) {
            toast.error("Por favor ingresa tu contraseña.")
            return
        }

        setLoading(true)
        try {
            const reAuthResult = await handleReauthenticate(password)

            if (reAuthResult.success) {
                // Después de reautenticar exitosamente, proceder con la eliminación completa
                await performAccountDeletion()
            } else {
                toast.error(reAuthResult.message || "Error al verificar la contraseña.")
                // Mantener el modal abierto para que el usuario pueda intentar de nuevo
            }
        } catch (error) {
            console.error("Error al reautenticar:", error)
            toast.error("Ocurrió un error al verificar tu contraseña.")
        } finally {
            setLoading(false)
            // Solo cerrar el modal si la eliminación fue exitosa
            // Si hay error, mantener abierto para que el usuario pueda reintentar
        }
    }

    const handleBeProvider = (type) => {
        const categoryType = type;
        navigate("/registro/proveedor", { state: { categoryType } })
    }

    const handleProviderTabClick = () => {
        if (!userInfoExists || !userInfo?.provider) {
            // Mostrar modal de registro en lugar de cambiar de pestaña
            setShowProviderRegisterModal(true)
            return
        }
        setActiveTab('proveedor')
    }

    const handleEditCancel = () => {
        // restaurar valores originales
        if (userInfo) {
            setFormData({
                name: userInfo.name || "",
                location: userInfo.location || "",
                birthDate: userInfo.birthDate || "",
                email: userInfo.email || "",
                locationCoordinates: null,
            })
        }
        setIsEditing(false)
    }

    const validateForm = (data) => {
        const errors = {
            name: !data.name || !data.name.trim() ? "El nombre es obligatorio" : null,
            location: !data.location || !data.location.trim() ? "La ubicación es obligatoria" : null,
            birthDate: !data.birthDate || !data.birthDate.trim() ? "La fecha de nacimiento es obligatoria" : null,
        }
        setFormErrors(errors)
        const ok = !errors.name && !errors.location && !errors.birthDate
        setIsValid(ok)
        return ok
    }

    useEffect(() => {
        if (isEditing) {
            validateForm(formData)
        } else {
            // cuando no edita, no mostrar errores
            setFormErrors({ name: null, location: null, birthDate: null })
            setIsValid(false)
        }
    }, [formData, isEditing])

    const handleFieldChange = (e) => {
        const { name, value } = e.target
        const next = { ...formData, [name]: value }
        setFormData(next)
        // no validar aquí para evitar setState redundante, lo hará el useEffect
    }

    const handleInlineLocationChange = async (locationName, coordinates = null, isSystemLocation = false) => {
        // Si estamos escribiendo una ubicación personalizada, actualizar inmediatamente sin procesamiento adicional
        if (locationName && locationName !== 'custom' && !coordinates) {
            setFormData(prev => ({ ...prev, location: locationName }))
            return
        }

        if (coordinates && !isSystemLocation) {
            try {
                const detectionResult = await detectLocationFromGPS(coordinates.latitude, coordinates.longitude)
                if (detectionResult.success) {
                    setFormData(prev => ({ ...prev, location: detectionResult.displayName, locationCoordinates: coordinates }))
                    return
                }
            } catch (err) { /* noop */ }
        }
        setFormData(prev => ({ ...prev, location: locationName, locationCoordinates: coordinates || prev.locationCoordinates }))
    }

    const handleInlineCoordinatesChange = (coordinates) => {
        setFormData(prev => ({ ...prev, locationCoordinates: coordinates }))
    }

    const handleEditSave = async () => {
        // Validación previa
        if (!validateForm(formData)) {
            toast.error("Completa todos los campos obligatorios")
            return
        }
        setLoading(true)
        setIsUpdatingProfile(true)
        try {
            const { name, birthDate, email, locationCoordinates } = formData
            let smartLocation = null
            if (locationCoordinates) {
                const detectionResult = await detectLocationFromGPS(locationCoordinates.latitude, locationCoordinates.longitude)
                if (detectionResult.success) {
                    smartLocation = generateSmartLocation(detectionResult, locationCoordinates)
                }
            }
            if (!smartLocation) {
                smartLocation = SmartLocationService.generateManualSmartLocation(formData.location)
            }
            const payload = { name, email, birthDate, smartLocation }
            await editUserInfo(payload)
            const updatedUserData = { ...getUserData(), name, email, birthDate, smartLocation }
            saveUserData(updatedUserData)
            setUserData(updatedUserData)
            toast.success("Perfil actualizado correctamente")
            // Refrescar userInfo en UI
            setUserInfo(prev => ({ ...prev, name, email, birthDate, location: smartLocation.displayName }))
            // Asegurar que permanezca en pestaña usuario después de guardar
            setActiveTab('usuario')
            setIsEditing(false)

            // Protección adicional: asegurar que permanezca en usuario después de un pequeño delay
            setTimeout(() => {
                setActiveTab('usuario')
            }, 100)
        } catch (error) {
            console.error("Error al guardar perfil inline:", error)
            toast.error("Error al actualizar el perfil")
        } finally {
            setLoading(false)
            setIsUpdatingProfile(false)
        }
    }

    useEffect(() => {
        // Solo establecer la pestaña inicial cuando se carga userInfo por primera vez
        // No cambiar cuando se actualiza userInfo por edición de datos, si ya se inicializó, o si se está actualizando el perfil
        if (!initialTabSet && userInfo && !profileLoading && !isEditing && !isUpdatingProfile) {
            if (userInfo.provider) {
                setActiveTab('proveedor')
            } else {
                setActiveTab('usuario')
            }
            setInitialTabSet(true)
        }
    }, [userInfo, initialTabSet, profileLoading, isEditing, isUpdatingProfile])

    if (loading || profileLoading) {
        return <Spinner />
    }

    if (!user) {
        return (
            <div className="container">
                <div className="user-info-card">
                    <h5>No se encontró información del usuario.</h5>
                </div>
            </div>
        )
    }

    // Ensure userInfo exists before rendering
    const userInfoExists = userInfo !== null && userInfo !== undefined

    return (
        <>
            {/* Contenedor principal con layout mejorado */}
            <div className="user-profile-container profile-layout">
                {/* Tabs verticales (desktop) */}
                <aside className="profile-sidebar">
                    <div className="profile-vertical-tabs" role="tablist" aria-label="Secciones de perfil">
                        <button
                            role="tab"
                            aria-selected={activeTab === 'usuario'}
                            className={`profile-vertical-tab ${activeTab === 'usuario' ? 'active' : ''}`}
                            onClick={() => setActiveTab('usuario')}
                        >
                            <span className="profile-tab-title">Usuario</span>
                            <span className="profile-tab-subtitle">Datos personales, favoritos</span>
                        </button>
                        <button
                            role="tab"
                            aria-selected={activeTab === 'proveedor'}
                            className={`profile-vertical-tab ${activeTab === 'proveedor' ? 'active' : ''}`}
                            onClick={handleProviderTabClick}
                        >
                            <span className="profile-tab-title">Proveedor</span>
                            <span className="profile-tab-subtitle">Perfil y panel de control</span>
                        </button>
                        <button
                            role="tab"
                            aria-selected={activeTab === 'resenas'}
                            className={`profile-vertical-tab ${activeTab === 'resenas' ? 'active' : ''}`}
                            onClick={() => setActiveTab('resenas')}
                        >
                            <span className="profile-tab-title">Mis reseñas</span>
                            <span className="profile-tab-subtitle">
                                {userInfo?.totalReviewsGiven > 0 ? `${userInfo.totalReviewsGiven} reseña${userInfo.totalReviewsGiven !== 1 ? 's' : ''}` : 'Tus valoraciones'}
                            </span>
                        </button>
                    </div>
                </aside>

                {/* Área principal de contenido */}
                <main className="profile-content" role="tabpanel">
                    {/* Tabs horizontales (solo móvil) ahora cerca del contenido */}
                    <div className="profile-tabs-header mobile-only near-content">
                        <button
                            className={`profile-tab-btn ${activeTab === 'usuario' ? 'active' : ''}`}
                            onClick={() => setActiveTab('usuario')}
                        >
                            Usuario
                        </button>
                        <button
                            className={`profile-tab-btn ${activeTab === 'proveedor' ? 'active' : ''}`}
                            onClick={handleProviderTabClick}
                        >
                            Proveedor
                        </button>
                        <button
                            className={`profile-tab-btn ${activeTab === 'resenas' ? 'active' : ''}`}
                            onClick={() => setActiveTab('resenas')}
                        >
                            Mis reseñas {userInfo?.totalReviewsGiven > 0 && <span className="tab-badge">{userInfo.totalReviewsGiven}</span>}
                        </button>
                    </div>

                    {activeTab === 'usuario' && (
                        <div className="profile-section profile-user-section">
                            <div className="profile-card">
                                <div className="profile-card-header">
                                    <h2 className="profile-heading">Perfil de Usuario</h2>
                                    <button
                                        onClick={handleNotificationsClick}
                                        className="profile-icon-btn accent"
                                        title="Ver notificaciones"
                                        aria-label="Ver notificaciones"
                                    >
                                        <Bell size={18} />
                                        <span className="notif-label desktop-only">Ver notificaciones</span>
                                    </button>
                                </div>
                                <div className="profile-card-body">
                                    {!userInfoExists && !profileLoading ? (
                                        <div className="profile-empty">
                                            <p>No se pudo cargar la información del usuario.</p>
                                            <button onClick={() => window.location.reload()} className="new-btn btn-outline-gray">
                                                Reintentar
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="profile-data-grid">
                                                <div className="profile-data-row">
                                                    <span className="label">Nombre</span>
                                                    {isEditing ? (
                                                        <>
                                                            <input
                                                                name="name"
                                                                type="text"
                                                                value={formData.name}
                                                                onChange={handleFieldChange}
                                                                className="form-control"
                                                            />
                                                            {formErrors.name && <span className="field-error">{formErrors.name}</span>}
                                                        </>
                                                    ) : (
                                                        <span className="value">{userInfoExists ? userInfo.name : 'Cargando...'}</span>
                                                    )}
                                                </div>
                                                <div className="profile-data-row">
                                                    <span className="label">Email</span>
                                                    <span className="value email-value">{userInfoExists ? userInfo.email : 'Cargando...'}</span>
                                                </div>
                                                <div className="profile-data-row">
                                                    <span className="label">Ubicación</span>
                                                    {isEditing ? (
                                                        <>
                                                            <GeolocationField
                                                                value={formData.location}
                                                                onChange={handleInlineLocationChange}
                                                                onCoordinatesChange={handleInlineCoordinatesChange}
                                                                label="Ubicación"
                                                                required={true}
                                                                allowCustom={false}
                                                                showCoordinatesOption={false}
                                                            />
                                                            {formErrors.location && <span className="field-error">{formErrors.location}</span>}
                                                        </>
                                                    ) : (
                                                        <span className="value">{userInfoExists ? userInfo.location : 'Cargando...'}</span>
                                                    )}
                                                </div>
                                                <div className="profile-data-row">
                                                    <span className="label">Fecha de Nacimiento</span>
                                                    {isEditing ? (
                                                        <>
                                                            <input
                                                                name="birthDate"
                                                                type="date"
                                                                value={formData.birthDate}
                                                                onChange={handleFieldChange}
                                                                className="form-control"
                                                            />
                                                            {formErrors.birthDate && <span className="field-error">{formErrors.birthDate}</span>}
                                                        </>
                                                    ) : (
                                                        <span className="value">{userInfoExists ? userInfo.birthDate : 'Cargando...'}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="profile-actions-row">
                                                {!isEditing ? (
                                                    <button onClick={() => { setIsEditing(true); }} className="new-btn btn-accent">Editar Información Personal</button>
                                                ) : (
                                                    <>
                                                        <button onClick={handleEditSave} className="new-btn btn-accent" disabled={!isValid}>Guardar Cambios</button>
                                                        <button onClick={handleEditCancel} className="new-btn btn-muted">Cancelar</button>
                                                    </>
                                                )}
                                                {!isEditing && (
                                                    <button onClick={onPasswordReset} className="new-btn btn-muted">Restablecer Contraseña</button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Favoritos */}
                            <div className="profile-card">
                                <div className="profile-card-header">
                                    <h3 className="profile-subheading">Proveedores Favoritos</h3>
                                </div>
                                <div className="profile-card-body">
                                    {userInfoExists && userInfo.favProviders && userInfo.favProviders.length > 0 ? (
                                        <ul className="profile-fav-list">
                                            {userInfo.favProviders.map((provider, index) => (
                                                <li className="profile-fav-item" key={index} onClick={() => handleProviderClick(provider)}>
                                                    <span className="fav-text">{provider}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="profile-empty">
                                            <p>{userInfoExists ? 'No tenés proveedores favoritos aún.' : 'Cargando favoritos...'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Posts del Usuario */}
                            <div className="profile-forum-posts-card">
                                <div className="profile-card-header">
                                    <h3 className="profile-subheading">Mis Posts del Foro</h3>
                                </div>
                                <div className="profile-card-body">
                                    <UserPosts />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'proveedor' && userInfo?.provider && (                        <div className="profile-section profile-provider-section">
                            <div className="profile-card">
                                <div className="profile-card-header">
                                    <h2 className="profile-heading">Perfil de Proveedor</h2>
                                </div>
                                <div className="profile-card-body">
                                    {userInfoExists && userInfo.provider ? (
                                        <>
                                            <div className="profile-data-grid">
                                                <div className="profile-data-row clickable" onClick={() => handleProviderClick(userInfo.providerRef, userInfo.providerType)}>
                                                    <span className="label">Proveedor Asociado</span>
                                                    <span className="value strong">{userInfo.providerRef}</span>
                                                </div>
                                                <div className="profile-data-row">
                                                    <span className="label">Tipo</span>
                                                    <span className="value">{userInfo.providerType === 'service' ? 'Servicios' : 'Productos'}</span>
                                                </div>
                                            </div>
                                            <div className="profile-actions-row provider">
                                                <ExportPdfButton providerId={userInfo.providerRef} type={userInfo.providerType} />
                                                <button className="new-btn" style={{ backgroundColor: '#555555' }} onClick={() => handleEditProvider(userInfo.providerRef, userInfo.providerType)}>Editar Proveedor</button>
                                                <button onClick={() => setShowConfirmDelete(true)} className="new-btn" style={{ backgroundColor: '#cc1e00' }}>Eliminar Proveedor</button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="profile-empty">
                                            <p>No tenés proveedor asociado.</p>
                                            {userInfoExists && (
                                                <div className="profile-actions-column">
                                                    <button onClick={() => handleBeProvider('service')} className="new-btn" style={{ backgroundColor: '#cc1e00', color: '#f0f0f0' }}>
                                                        Quiero ser Proveedor de Servicios
                                                    </button>
                                                    <button onClick={() => handleBeProvider('products')} className="new-btn" style={{ backgroundColor: '#0056b3', color: '#f0f0f0' }}>
                                                        Quiero ser Proveedor de Productos
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Beneficios Partners — visible para cualquier provider, no solo partners */}
                            {userInfoExists && userInfo.provider && (
                                <PartnerBenefitsBox />
                            )}

                            {/* Panel de Control con métricas reales */}
                            {userInfoExists && userInfo.provider && userInfo.providerRef && (
                                <ProviderDashboardBox providerId={userInfo.providerRef} />
                            )}
                        </div>
                    )}

                    {/* ===================== TAB: MIS RESEÑAS ===================== */}
                    {activeTab === 'resenas' && (
                        <div className="profile-section profile-reviews-section">
                            <div className="profile-card">
                                <div className="profile-card-header">
                                    <h2 className="profile-heading">Mis reseñas</h2>
                                    {userInfo?.totalReviewsGiven > 0 && (
                                        <span className="profile-reviews-count">
                                            {userInfo.totalReviewsGiven} reseña{userInfo.totalReviewsGiven !== 1 ? 's' : ''} dejada{userInfo.totalReviewsGiven !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                                <div className="profile-card-body">
                                    {!userInfoExists || !userInfo?.reviews || userInfo.reviews.length === 0 ? (
                                        <div className="profile-empty">
                                            <Star size={40} strokeWidth={1.2} style={{ opacity: 0.3, marginBottom: 8 }} />
                                            <p>Todavía no dejaste ninguna reseña.</p>
                                            <p style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                                                Cuando visites un proveedor, podrás calificarlo con estrellas.
                                            </p>
                                        </div>
                                    ) : (
                                        <ul className="user-reviews-list">
                                            {userInfo.reviews.map((review) => (
                                                <li key={review.id} className="user-review-item">
                                                    {/* Encabezado: proveedor + fecha */}
                                                    <div className="user-review-header">
                                                        <button
                                                            className="user-review-provider-link"
                                                            onClick={() => handleProviderClick(review.providerId)}
                                                            title={`Ver perfil de ${review.providerId}`}
                                                        >
                                                            @{review.providerId}
                                                        </button>
                                                        <span className="user-review-date">
                                                            {new Date(review.date).toLocaleDateString('es-ES', {
                                                                day: '2-digit', month: '2-digit', year: 'numeric'
                                                            })}
                                                        </span>
                                                    </div>

                                                    {/* Estrellas */}
                                                    <div className="user-review-stars">
                                                        <UserStarRow value={review.rating} />
                                                        <span className="user-review-rating-num">{review.rating}/5</span>
                                                    </div>

                                                    {/* Comentario */}
                                                    {review.comment && (
                                                        <p className="user-review-comment">{review.comment}</p>
                                                    )}

                                                    {/* Respuesta del proveedor */}
                                                    {review.providerResponse && (
                                                        <div className="user-review-response">
                                                            <CornerDownRight size={15} className="user-review-response-icon" />
                                                            <p className="user-review-response-text">{review.providerResponse}</p>
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {/* Barra global centrada para eliminar cuenta (visible solo en pestaña usuario) */}
            {activeTab === 'usuario' && (
                <div className="profile-danger-bar">
                    <button className="profile-danger-btn soft" onClick={handleDeleteAccountClick}>
                        <Trash2 size={16} /> Eliminar cuenta
                    </button>
                </div>
            )}

            {/* Modales */}
            {showConfirmDelete && (
                <div className="popup-overlay">
                    <div className="popup">
                        <h3>¿Estás seguro?</h3>
                        <p>Eliminar tu perfil de proveedor es una acción irreversible. Tendrás que volver a registrar todos tus datos si decides ofrecer servicios nuevamente.</p>
                        <div className="popup-actions">
                            <button onClick={handleDeleteProvider} className="new-btn" style={{ marginRight: '4px', backgroundColor: '#cc1e00', color: '#f0f0f0' }}>
                                Sí, eliminar
                            </button>
                            <button onClick={() => setShowConfirmDelete(false)} className="new-btn btn-gray">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {showConfirmAccountDelete && (
                <div className="popup-overlay">
                    <div className="popup">
                        <h3 style={{ fontSize: '1.2rem' }}>¿Estás seguro que deseas eliminar tu cuenta?</h3>
                        <p style={{ fontSize: '1rem' }}>Esta acción es irreversible. Se eliminarán todos tus datos, incluyendo tu perfil de proveedor (en caso de serlo), tus favoritos y toda la información asociada a tu cuenta.</p>
                        {showPasswordField && (
                            <div className="password-confirmation">
                                <p className="password-info">Por seguridad, debes confirmar tu contraseña para continuar:</p>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Ingresa tu contraseña"
                                    className="password-input"
                                />
                                <button onClick={handlePasswordSubmit} className="new-btn btn-red" disabled={!password.trim()} style={{ width: '100%', marginTop: '10px' }}>
                                    Confirmar
                                </button>
                            </div>
                        )}
                        <div className="popup-actions">
                            <button onClick={confirmDeleteAccount} className="btn btn-red" style={{ marginRight: '4px' }} disabled={showPasswordField || isReauthenticating}>
                                {isReauthenticating ? 'Autenticando...' : 'Sí, eliminar'}
                            </button>
                            <button onClick={() => { setShowConfirmAccountDelete(false); setShowPasswordField(false); setPassword(''); }} className="btn btn-gray">
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && (
                <div className="popup-overlay">
                    <div className="popup edit-profile-popup">
                        <h3>Editar Perfil</h3>
                        <p>¿Qué querés editar?</p>
                        <div className="edit-profile-actions">
                            <button onClick={handleEditProviderInfo} className="new-btn" style={{ marginRight: '10px', backgroundColor: '#0056b3', color: '#f0f0f0' }}>
                                Editar Perfil de Proveedor
                            </button>
                            <button onClick={handleEditUserInfo} className="new-btn" style={{ backgroundColor: '#555555', color: '#f0f0f0' }}>
                                Editar Información Personal Privada
                            </button>
                        </div>
                        <button onClick={() => setShowEditModal(false)} className="btn btn-gray" style={{ marginTop: '10px' }}>
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {showProviderRegisterModal && (
                <div className="popup-overlay" onClick={() => setShowProviderRegisterModal(false)}>
                    <div className="popup provider-register-popup" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="modal-close-btn"
                            aria-label="Cerrar"
                            onClick={() => setShowProviderRegisterModal(false)}
                        >
                            ×
                        </button>
                        <h3>No estás registrado como proveedor</h3>
                        <p style={{ marginTop: '10px', color: '#888' }}>
                            Si tenés servicios o productos para ofrecer, podés crear tu perfil ahora ¡Permití que te encuentren donde te buscan!
                        </p>
                        <div className="provider-register-actions">
                            <button
                                onClick={() => navigate('/registro/servicios')}
                                className="new-btn btn-red"
                                style={{ backgroundColor: '#cc1e00', color: '#ffffff' }}
                            >
                                Registrar Servicios
                            </button>
                            <button
                                onClick={() => navigate('/registro/productos')}
                                className="new-btn btn-accent"
                                style={{ backgroundColor: '#0056b3', color: '#ffffff' }}
                            >
                                Registrar Productos
                            </button>
                        </div>
                        <button
                            onClick={() => setShowProviderRegisterModal(false)}
                            className="new-btn btn-muted"
                            style={{ marginTop: '12px' }}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

export default UserProfile
